import React, { type ComponentProps, useEffect, useMemo, useState } from 'react';
import { ChakraProvider, extendTheme } from '@chakra-ui/react';
import { Outlet, useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { withI18NextTranslate } from 'sp-ui';
import { getTheme } from 'ts/common/components/gallery';
import { IFetchHookResponse, useQuery } from 'ts/common/hooks';
import PageContainer from 'client_react/booking/PageContainer';
import { QUERY } from 'client_react/booking/common';
import { IBookingSessionAvailability, IBookingSessionType } from 'client_react/booking/types';
import useBookingSession from 'client_react/booking/useBookingSession';
import { useBootstrapData, useClientApiFetch, withBootstrapData } from 'client_react/bootstrap';
import useContract from 'client_react/common/hooks/useContract';
import useQuestionnaire from 'client_react/questionnaire/hooks/useQuestionnaire';

type SignatureData = {
    signaturePaths: string;
    svgViewbox: string;
};

export type ClientBookingData = ReturnType<typeof useBookingSession> &
    Omit<ReturnType<typeof useContract>, 'isPending'> &
    Pick<
        ReturnType<typeof useQuestionnaire>,
        | 'questionnaire'
        | 'saveQuestionResponse'
        | 'submitQuestionnaire'
        | 'saving'
        | 'requestFailed'
    > & {
        bookingSessionType: IFetchHookResponse<IBookingSessionType>;
        bookingSessionTypeAvailability: IFetchHookResponse<IBookingSessionAvailability>;
        signatureData: Nullable<SignatureData>;
        setSignatureData: (data: Nullable<SignatureData>) => void;
    };

const App = withBootstrapData(() => {
    const navigate = useNavigate();
    const { translations } = useBootstrapData();
    const PageContainerWithTranslations = useMemo(
        () =>
            withI18NextTranslate<ComponentProps<typeof PageContainer>>(
                ({ children, ...rest }: ComponentProps<typeof PageContainer>) => (
                    <PageContainer {...rest}>{children}</PageContainer>
                ),
                translations
            ),
        [translations]
    );

    const location = useLocation();

    const query = useQuery();
    const queryBookingSessionId = query.get(QUERY.BOOKING_SESSION_ID);

    const { sessionTypeSlug, bookingSessionId: paramBookingSessionId } = useParams();

    const bookingSessionId = location.pathname.startsWith('/session')
        ? paramBookingSessionId ?? null
        : queryBookingSessionId ?? null;

    const {
        bookingSession,
        createBookingSession,
        error,
        getBookingSession,
        heartbeatBookingSession,
        isPending,
        submitPayment,
        updateBookingSession
    } = useBookingSession(bookingSessionId);

    const {
        questionnaire,
        loading: isLoadingQuestionnaire,
        saveQuestionResponse,
        saving,
        submitQuestionnaire,
        submitting: isSubmittingQuestionnaire,
        requestFailed,
        error: questionnaireError
    } = useQuestionnaire({
        email: bookingSession?.contactEmail,
        questionnairePublicId: bookingSession?.questionnairePublicId
    });

    const {
        contract,
        createContract,
        getContract,
        isPending: isPendingContract,
        signContract,
        updateContract
    } = useContract(bookingSession?.contractId, bookingSession?.contactEmail);

    const { response: bookingSessionType, performFetch: fetchSessionType } =
        useClientApiFetch<IBookingSessionType>('booking-session-type', {
            defer: true
        });

    const currentDate = new Date().toISOString().split('T')[0];

    const { response: bookingSessionTypeAvailability, performFetch: fetchAvailability } =
        useClientApiFetch<IBookingSessionAvailability>('', {
            defer: true
        });

    useEffect(() => {
        if (!bookingSessionId || bookingSession?.isConfirmed) {
            return;
        }

        const expiredSessionCheckTimer = setInterval(() => {
            (async () => {
                const response = await getBookingSession();

                if (!!response && response.status === 404) {
                    navigate(`/booking/${sessionTypeSlug}?expired=1`);
                }
            })();
        }, 5000);

        return () => {
            clearInterval(expiredSessionCheckTimer);
        };
    }, [bookingSession, bookingSessionId, getBookingSession, navigate, sessionTypeSlug]);

    useEffect(() => {
        if (sessionTypeSlug) {
            fetchSessionType({ url: sessionTypeSlug });
        }
    }, [fetchSessionType, sessionTypeSlug]);

    useEffect(() => {
        if (bookingSessionType?.publicId) {
            fetchAvailability({
                url: `booking-session-type/${bookingSessionType?.publicId}/availability`
            });
        }
    }, [bookingSessionType, currentDate, fetchAvailability]);

    useEffect(() => {
        if (bookingSessionId && error?.status === 404) {
            navigate(`/booking/${sessionTypeSlug}?expired=1`);
        }

        if (
            (!bookingSessionId && !sessionTypeSlug) ||
            (sessionTypeSlug && bookingSessionType?.status === 404)
        ) {
            navigate('/not-found');
        }
    }, [
        bookingSession,
        bookingSessionId,
        bookingSessionType,
        error,
        navigate,
        queryBookingSessionId,
        sessionTypeSlug
    ]);

    const brandTheme = bookingSession?.brandTheme ?? bookingSessionType?.brandTheme;

    const theme = useMemo(
        () => (brandTheme ? extendTheme(getTheme(brandTheme)) : undefined),
        [brandTheme]
    );

    // Add signature data state
    const [signatureData, setSignatureData] = useState<Nullable<SignatureData>>(null);

    const clientBookingData: ClientBookingData = useMemo(
        () => ({
            bookingSessionType,
            bookingSessionTypeAvailability,
            bookingSession,
            contract,
            createBookingSession,
            createContract,
            error: error || questionnaireError,
            getBookingSession,
            getContract,
            heartbeatBookingSession,
            isPending:
                isPending ||
                isLoadingQuestionnaire ||
                isSubmittingQuestionnaire ||
                isPendingContract,
            signContract,
            submitPayment,
            updateBookingSession,
            updateContract,
            signatureData,
            setSignatureData,
            questionnaire,
            saveQuestionResponse,
            submitQuestionnaire,
            saving,
            requestFailed
        }),
        [
            bookingSessionType,
            bookingSessionTypeAvailability,
            bookingSession,
            contract,
            createBookingSession,
            createContract,
            error,
            getBookingSession,
            getContract,
            heartbeatBookingSession,
            isPending,
            isPendingContract,
            signContract,
            submitPayment,
            updateBookingSession,
            updateContract,
            signatureData,
            setSignatureData,
            questionnaireError,
            isLoadingQuestionnaire,
            isSubmittingQuestionnaire,
            questionnaire,
            saveQuestionResponse,
            submitQuestionnaire,
            saving,
            requestFailed
        ]
    );

    // something more elegant would be nice
    if (!theme) {
        return null;
    }

    return (
        <ChakraProvider resetCSS={false} theme={theme}>
            <PageContainerWithTranslations
                bookingSession={bookingSession}
                bookingSessionType={bookingSessionType}
            >
                <Outlet context={clientBookingData} />
            </PageContainerWithTranslations>
        </ChakraProvider>
    );
});

export function useClientBookingData() {
    return useOutletContext<ClientBookingData>();
}

export default App;
